/**
 * Enhanced Logging Service
 * 
 * Provides structured logging with multiple adapters,
 * log levels, and contextual information.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */

import type { Config } from '../../config/environment';

/**
 * Log levels enumeration
 */
export const LogLevel = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
} as const;

export type LogLevel = (typeof LogLevel)[keyof typeof LogLevel];

/**
 * Log entry structure
 */
export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  context?: string;
  metadata?: Record<string, unknown>;
  error?: Error;
  correlationId?: string;
}

/**
 * Log adapter interface
 */
export interface ILogAdapter {
  log(entry: LogEntry): void;
  flush?(): Promise<void>;
}

/**
 * Logger interface
 */
export interface ILogger {
  error(message: string, context?: string, metadata?: Record<string, unknown>, error?: Error): void;
  warn(message: string, context?: string, metadata?: Record<string, unknown>): void;
  info(message: string, context?: string, metadata?: Record<string, unknown>): void;
  debug(message: string, context?: string, metadata?: Record<string, unknown>): void;
  setLevel(level: LogLevel): void;
  addAdapter(adapter: ILogAdapter): void;
  removeAdapter(adapter: ILogAdapter): void;
  flush(): Promise<void>;
  child(context: string): ILogger;
}

/**
 * Console log adapter
 */
export class ConsoleLogAdapter implements ILogAdapter {
  private readonly isDevelopment: boolean;

  constructor(isDevelopment = false) {
    this.isDevelopment = isDevelopment;
  }

  public log(entry: LogEntry): void {
    const formattedMessage = this.formatMessage(entry);
    
    switch (entry.level) {
      case LogLevel.ERROR:
        console.error(formattedMessage, entry.error);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage);
        break;
      case LogLevel.INFO:
        // eslint-disable-next-line no-console
        console.info(formattedMessage);
        break;
      case LogLevel.DEBUG:
        if (this.isDevelopment) {
          // eslint-disable-next-line no-console
          console.log(formattedMessage);
        }
        break;
    }
  }

  private formatMessage(entry: LogEntry): string {
    const timestamp = entry.timestamp.toISOString();
    const level = Object.keys(LogLevel).find(key => LogLevel[key as keyof typeof LogLevel] === entry.level) || 'UNKNOWN';
    const context = entry.context ? ` [${entry.context}]` : '';
    const correlationId = entry.correlationId ? ` (${entry.correlationId})` : '';
    const metadata = entry.metadata ? ` ${JSON.stringify(entry.metadata)}` : '';
    
    return `[${timestamp}] ${level}${context}${correlationId}: ${entry.message}${metadata}`;
  }
}

/**
 * Memory log adapter for testing
 */
export class MemoryLogAdapter implements ILogAdapter {
  private logs: LogEntry[] = [];

  public log(entry: LogEntry): void {
    this.logs.push({ ...entry });
  }

  public getLogs(level?: LogLevel): LogEntry[] {
    return level !== undefined 
      ? this.logs.filter(log => log.level === level)
      : [...this.logs];
  }

  public clear(): void {
    this.logs = [];
  }

  public getLogCount(level?: LogLevel): number {
    return this.getLogs(level).length;
  }
}

/**
 * Remote log adapter for production logging
 */
export class RemoteLogAdapter implements ILogAdapter {
  private buffer: LogEntry[] = [];
  private readonly endpoint: string;
  private readonly batchSize: number;
  private readonly flushInterval: number;
  private flushTimer?: NodeJS.Timeout;

  constructor(endpoint: string, batchSize = 10, flushInterval = 30000) {
    this.endpoint = endpoint;
    this.batchSize = batchSize;
    this.flushInterval = flushInterval;
    this.startFlushTimer();
  }

  public log(entry: LogEntry): void {
    this.buffer.push({ ...entry });
    
    if (this.buffer.length >= this.batchSize) {
      this.flush().catch(console.error);
    }
  }

  public async flush(): Promise<void> {
    if (this.buffer.length === 0) {return;}

    const logsToSend = [...this.buffer];
    this.buffer = [];

    try {
      await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs: logsToSend }),
      });
    } catch (error) {
      // Put logs back in buffer on failure
      this.buffer.unshift(...logsToSend);
      console.error('Failed to send logs to remote endpoint:', error);
    }
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush().catch(console.error);
    }, this.flushInterval);
  }

  public destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush().catch(console.error);
  }
}

/**
 * Enhanced Logger implementation
 */
export class Logger implements ILogger {
  private currentLevel: LogLevel;
  private adapters: Set<ILogAdapter> = new Set();
  private correlationId?: string;

  constructor(
    level: LogLevel = LogLevel.INFO,
    adapters: ILogAdapter[] = []
  ) {
    this.currentLevel = level;
    adapters.forEach(adapter => this.addAdapter(adapter));
  }

  /**
   * Set correlation ID for request tracking
   */
  public setCorrelationId(id: string): void {
    this.correlationId = id;
  }

  /**
   * Clear correlation ID
   */
  public clearCorrelationId(): void {
    this.correlationId = undefined;
  }

  /**
   * Log error message
   */
  public error(
    message: string, 
    context?: string, 
    metadata?: Record<string, unknown>, 
    error?: Error
  ): void {
    this.log(LogLevel.ERROR, message, context, metadata, error);
  }

  /**
   * Log warning message
   */
  public warn(
    message: string, 
    context?: string, 
    metadata?: Record<string, unknown>
  ): void {
    this.log(LogLevel.WARN, message, context, metadata);
  }

  /**
   * Log info message
   */
  public info(
    message: string, 
    context?: string, 
    metadata?: Record<string, unknown>
  ): void {
    this.log(LogLevel.INFO, message, context, metadata);
  }

  /**
   * Log debug message
   */
  public debug(
    message: string, 
    context?: string, 
    metadata?: Record<string, unknown>
  ): void {
    this.log(LogLevel.DEBUG, message, context, metadata);
  }

  /**
   * Set log level
   */
  public setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }

  /**
   * Add log adapter
   */
  public addAdapter(adapter: ILogAdapter): void {
    this.adapters.add(adapter);
  }

  /**
   * Remove log adapter
   */
  public removeAdapter(adapter: ILogAdapter): void {
    this.adapters.delete(adapter);
  }

  /**
   * Flush all adapters
   */
  public async flush(): Promise<void> {
    const flushPromises = Array.from(this.adapters)
      .filter(adapter => adapter.flush)
      .map(adapter => adapter.flush!());
    
    await Promise.all(flushPromises);
  }

  /**
   * Create child logger with context
   */
  public child(context: string): ILogger {
    const childLogger = new Logger(this.currentLevel, Array.from(this.adapters));
    if (this.correlationId) {
      childLogger.setCorrelationId(this.correlationId);
    }
    
    // Override methods to include context
    const originalError = childLogger.error.bind(childLogger);
    const originalWarn = childLogger.warn.bind(childLogger);
    const originalInfo = childLogger.info.bind(childLogger);
    const originalDebug = childLogger.debug.bind(childLogger);

    childLogger.error = (message, ctx, metadata, error) => 
      originalError(message, ctx || context, metadata, error);
    childLogger.warn = (message, ctx, metadata) => 
      originalWarn(message, ctx || context, metadata);
    childLogger.info = (message, ctx, metadata) => 
      originalInfo(message, ctx || context, metadata);
    childLogger.debug = (message, ctx, metadata) => 
      originalDebug(message, ctx || context, metadata);

    return childLogger;
  }

  /**
   * Internal log method
   */
  private log(
    level: LogLevel,
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
    error?: Error
  ): void {
    if (level > this.currentLevel) {return;}

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date(),
      context,
      metadata,
      error,
      correlationId: this.correlationId,
    };

    this.adapters.forEach(adapter => {
      try {
        adapter.log(entry);
      } catch (adapterError) {
        console.error('Log adapter error:', adapterError);
      }
    });
  }
}

/**
 * Logger factory
 */
export class LoggerFactory {
  public static create(config: Config): ILogger {
    const level = this.parseLogLevel(config.LOG_LEVEL);
    const adapters: ILogAdapter[] = [];

    // Always add console adapter
    adapters.push(new ConsoleLogAdapter(config.NODE_ENV === 'development'));

    // Add remote adapter in production
    if (config.NODE_ENV === 'production' && config.APP_URL) {
      adapters.push(new RemoteLogAdapter(`${config.APP_URL}/api/logs`));
    }

    return new Logger(level, adapters);
  }

  private static parseLogLevel(level: string): LogLevel {
    switch (level.toLowerCase()) {
      case 'error': return LogLevel.ERROR;
      case 'warn': return LogLevel.WARN;
      case 'info': return LogLevel.INFO;
      case 'debug': return LogLevel.DEBUG;
      default: return LogLevel.INFO;
    }
  }
} 